import React from "react";
import { Link } from "react-router-dom";
import { Coffee, Heart, ArrowLeft } from "lucide-react";
import { PublicLayout } from "@/components/site/PublicLayout";
import { CohnectMark } from "@/components/site/CohnectMark";
import { BRAND } from "@/constants/brand";

/**
 * Dedicated, distraction-free support landing page.
 * This is the destination for the in-app "support" link and funnels visitors
 * straight to ko-fi.com/cohnect (opens in a new tab). No payments on-site.
 */
export default function SupportPage() {
  return (
    <PublicLayout>
      <section className="relative overflow-hidden hero-wash">
        <CohnectMark
          className="pointer-events-none absolute -right-20 -top-24 w-[460px] text-[color:var(--coh-ink)] opacity-[0.05]"
        />
        <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="nb-box p-6 sm:p-10" data-testid="support-page">
            <div className="kicker inline-flex items-center gap-2"><Coffee className="h-3.5 w-3.5" /> Support Cohnect</div>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-[-0.03em] leading-[1.03] text-[color:var(--coh-ink)]">
              Like Cohnect? Buy a coffee.
            </h1>
            <p className="mt-5 text-lg text-[color:var(--coh-ink-muted)] leading-relaxed">
              Cohnect is free — the app and the weekly letter, always. If it’s helped you feel a little
              more connected, a tip on Ko-fi is a simple way to show your support and help fuel future
              updates, ideas, and improvements.
            </p>

            <a
              href={BRAND.kofiUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Support Cohnect on Ko-fi"
              data-testid="support-page-kofi-card"
              className="mt-8 block border-2 border-[color:var(--coh-ink)] shadow-nb overflow-hidden transition-[transform,box-shadow] duration-200 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-nb-hover"
            >
              <img src="/brand/kofi-card.jpg" alt="Support Cohnect on ko-fi.com/cohnect" className="block w-full h-auto" />
            </a>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={BRAND.kofiUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="support-page-kofi-button"
                className="btn-nb btn-nb-primary inline-flex h-14 items-center px-8 text-lg font-semibold"
              >
                <Coffee className="h-5 w-5 mr-2" /> Support on Ko-fi <span aria-hidden="true" className="font-mono ml-1">↗</span>
              </a>
              <span className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.14em] text-[color:var(--coh-ink-muted)]">
                <Heart className="h-4 w-4 text-[color:var(--coh-blue)]" /> Cohnect stays free
              </span>
            </div>

            <p className="mt-6 text-xs text-[color:var(--coh-ink-muted)] font-mono">
              Donations are handled securely by Ko-fi. Cohnect never collects your email or payment
              details on this site.
            </p>
          </div>

          <div className="mt-8">
            <Link to="/" data-testid="support-back-home" className="inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--coh-ink)] underline-offset-4 hover:underline hover:text-[color:var(--coh-blue)]">
              <ArrowLeft className="h-4 w-4" /> Back to Cohnect
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
